import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  input,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, once } from '@web/utils';
import { Observable } from 'rxjs';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { HttpErrorResponse } from '@angular/common/http';
import { WarehouseRestockFormComponent } from '../../components/warehouse-restock-form/warehouse-restock-form.component';
import { WarehouseRestockFormData } from '../../models/warehouse-restock-form-data';
import { WarehouseRestockDataSourceService } from '../../services/warehouse-restock-data-source.service';

@Component({
  selector: 'app-warehouse-restock-edit',
  imports: [
    CommonModule,
    SpinnerFilledComponent,
    AlertsComponent,
    WarehouseRestockFormComponent,
  ],
  templateUrl: './warehouse-restock-edit.component.html',
  styleUrl: './warehouse-restock-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class WarehouseRestockEditComponent {
  /* prettier-ignore */
  private readonly warehouseRestockDataSource = inject(WarehouseRestockDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public readonly id = input.required<Guid>();
  public readonly fileUploadVisibility = input(false);
  public readonly submitSuccessEvent = output();

  protected data$!: Observable<WarehouseRestockFormData>;

  public constructor() {
    effect(() => this.configureDataSource());
  }

  public handleSubmitSuccessEvent(data: WarehouseRestockFormData): void {
    this.warehouseRestockDataSource
      .updateByFormData(this.id(), data)
      .pipe(once())
      .subscribe({
        next: () => {
          this.notifySubmitSuccessEvent();
        },
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private notifySubmitSuccessEvent(): void {
    this.submitSuccessEvent.emit();
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    this.alerts.showMultiple((builder) =>
      this.httpError
        .extract(response)
        .messages()
        .map((message) => builder.buildFailure(message)),
    );
  }

  private configureDataSource(): void {
    this.data$ = this.warehouseRestockDataSource.fetchDetailsFormById(
      this.id(),
    );

    this.changeDetectorRef.detectChanges();
  }
}
